import type { ReactNode } from "react";
import "./DataTable.css";

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string | ((row: T) => string | undefined);
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Row is a whole-row click target (e.g. navigate to detail) in addition
   * to whatever real link/button a cell renders — the row click is a mouse
   * convenience, keyboard/screen-reader users still have the real control. */
  onRowClick?: (row: T) => void;
  /** Reflects a background refetch without swapping to the loading state
   * (e.g. `isFetching` while a cursor page reloads). */
  ariaBusy?: boolean;
}

/**
 * Shared table chrome for every list view (review FE-L: "no DataTable
 * component despite the CSS"). Column-driven so each page keeps its own cell
 * markup (links, pills, mono formatting) without re-declaring the
 * table/thead/tbody wrapper and its accessibility attributes each time.
 */
export function DataTable<T>({ columns, rows, rowKey, onRowClick, ariaBusy }: DataTableProps<T>) {
  return (
    <div className="data-table-wrapper" aria-busy={ariaBusy}>
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} scope="col">
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={onRowClick ? "is-clickable" : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              // A row-click is a mouse convenience on top of whatever real link/button a
              // cell renders (see `onRowClick`'s doc comment) — it must be just as
              // reachable by keyboard (audit item: rows were mouse-only). `tabIndex={0}`
              // plus an Enter/Space handler makes the row itself keyboard-activatable
              // without changing its accessible role away from a plain table row (a
              // `<tr>` already has an implicit `row` role — overriding it to `button`
              // would break every screen-reader/table-navigation affordance the real
              // `<table>` markup provides for what is still, structurally, a row).
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      onRowClick(row);
                    }
                  : undefined
              }
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={typeof col.className === "function" ? col.className(row) : col.className}
                >
                  {col.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
